import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { getCreditInfoByStore } from '../../api/credit.api';
import { getMyPaymentStatus, type LicensePaymentStatus } from '../../api/license.api';
import LicensePaymentBanner from '../license/LicensePaymentBanner';
import LicenseLockedScreen from '../license/LicenseLockedScreen';
import styles from './Layout.module.css';

interface LayoutProps {
  title?: string;
  children: React.ReactNode;
}

export default function Layout({ title, children }: LayoutProps) {
  const { isDemo, storeId, logout } = useAuth();
  const location = useLocation();
  const [paymentStatus, setPaymentStatus] = useState<LicensePaymentStatus | null>(null);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const loadPaymentStatus = useCallback(async () => {
    if (!storeId || isDemo) return;
    try {
      const info = await getCreditInfoByStore(storeId);
      const status = await getMyPaymentStatus(info.licenseId);
      setPaymentStatus(status);
    } catch {
      // 조회 실패 시 차단하지 않음 (네트워크 오류 등)
      setPaymentStatus(null);
    }
  }, [storeId, isDemo]);

  useEffect(() => {
    loadPaymentStatus();
    // 5분마다 재조회 (입금 확인 후 즉시 잠금 해제 반영)
    const refresh = setInterval(loadPaymentStatus, 5 * 60_000);
    return () => clearInterval(refresh);
  }, [loadPaymentStatus]);

  // 페이지 이동 시 모바일 메뉴 자동 닫힘
  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [location.pathname]);

  // 모바일 메뉴 열린 상태에서 body 스크롤 잠금
  useEffect(() => {
    if (isMobileNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileNavOpen]);

  // 라이선스 잠금 상태 → 화면 자체 차단
  if (paymentStatus?.isLocked) {
    return <LicenseLockedScreen status={paymentStatus} onLogout={logout} />;
  }

  return (
    <div className={styles.layout}>
      <Sidebar
        isMobileOpen={isMobileNavOpen}
        onMobileClose={() => setIsMobileNavOpen(false)}
      />
      {/* 모바일 백드롭 (사이드바 열렸을 때만) */}
      {isMobileNavOpen && (
        <div
          className={styles.mobileBackdrop}
          onClick={() => setIsMobileNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <main className={styles.content}>
        {/* 모바일 상단 헤더 (햄버거 + 페이지 제목) */}
        <div className={styles.mobileHeader}>
          <button
            type="button"
            className={styles.hamburger}
            onClick={() => setIsMobileNavOpen(true)}
            aria-label="메뉴 열기"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <span className={styles.mobileBrand}>만석</span>
        </div>

        {isDemo && (
          <div className={styles.demoBanner}>
            👁️ <b>데모 모드</b> — 화면 확인만 가능합니다. 버튼/입력은 작동하지 않습니다.
          </div>
        )}
        <LicensePaymentBanner />
        {title && <h1 className={styles.pageTitle}>{title}</h1>}
        {isDemo ? (
          <div className={styles.demoFrame}>
            {children}
            <div className={styles.demoOverlay} aria-hidden="true" />
          </div>
        ) : (
          children
        )}
      </main>
    </div>
  );
}
